import React, { useCallback } from "react";

import { useMutation } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Theme, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { CreateRecipeInput } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { gtagEvent } from "@/utils/gtag";

import RecipeForm, { RecipeFormValues } from "./components/RecipeForm";
import { recipesPageQuery } from "../Index/RecipesPage";

const Root = withStyles(Box, (theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    position: "relative",
    flex: 1,
    paddingTop: theme.spacing(2),
    height: "100%",
  },
}));

const initialValues: RecipeFormValues = {
  name: "",
  preparationMinutes: "" as unknown as number,
  portions: "" as unknown as number,
  description: "",
  ingredients: [],
  instructions: [],
  meta: {
    activeStep: 0,
    completedSteps: {},
  },
  selectedIngredient: null,
};

const createRecipeMutation = graphql(`
  mutation CreateRecipe($input: CreateRecipeInput!) {
    createRecipe(input: $input) {
      recipe {
        ...RecipeListItem
      }
    }
  }
`);

const CreateRecipePage: React.FC = () => {
  const navigate = useNavigate();

  const [createRecipe] = useMutation(createRecipeMutation);

  const saveRecipe = useCallback(
    async (input: CreateRecipeInput) => {
      await createRecipe({
        variables: { input },
        refetchQueries: [recipesPageQuery],
      });
      gtagEvent("create_recipe");
    },
    [createRecipe]
  );

  const redirectToRecipes = useCallback(
    () => navigate("/database/recipes"),
    []
  );

  return (
    <RecipeForm
      isInitialValid={false}
      initialValues={initialValues}
      onSubmit={saveRecipe}
      afterSubmit={redirectToRecipes}
      onCancel={redirectToRecipes}
    >
      {({ renderStepper, renderForm }) => (
        <Root>
          <Box>
            <Button
              startIcon={<ArrowBackIcon fontSize="small" color="primary" />}
              variant="text"
              onClick={redirectToRecipes}
            >
              <Typography variant="body2" color={colors.black} fontWeight={400}>
                Back to recipes
              </Typography>
            </Button>
          </Box>
          <Box
            position="absolute"
            top={58}
            display="flex"
            justifyContent="center"
            width="100%"
            zIndex={10}
          >
            {renderStepper()}
          </Box>
          {renderForm()}
        </Root>
      )}
    </RecipeForm>
  );
};

export default CreateRecipePage;
